import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { ImageProps, getTokenValue, Token, styled } from 'tamagui'

import { Image } from '../Image'

import { IMAGEKIT_URL } from '../../data/config/url'

import { UserPrimaryPhotoNew_user$key } from './__generated__/UserPrimaryPhotoNew_user.graphql'

const DEFAULT_AVATARS = [
  'default-avatar-1_5QE9rI7Ha3.png',
  'default-avatar-2_DT5LXnCTf.png',
  'default-avatar-3_NTdhIFeg2.png',
  'default-avatar-4_QrxDP2X-N.png',
]

const UserPrimaryPhotoFrame = styled(Image, {
  name: 'UserPrimaryPhoto',
  boxShadow: '0 0 0 2px #FFFFFF',
  borderColor: 'rgba(19, 0, 102, 0.16)',
  borderWidth: 1,
})

export const UserPrimaryPhoto = React.forwardRef(
  (
    {
      user,
      size = '$5',
      ...props
    }: {
      user: UserPrimaryPhotoNew_user$key
      size: Token
    },
    ref
  ) => {
    const data = useFragment(
      graphql`
        fragment UserPrimaryPhotoNew_user on User
        @argumentDefinitions(size: { type: "Int", defaultValue: 200 }) {
          rowId
          gravatarImageUrl(size: $size)

          primaryPhoto {
            rowId
            contentType
          }
        }
      `,
      user
    )

    const parsedSize = useMemo(() => {
      return getTokenValue(size, 'size')
    }, [size])

    if (data.primaryPhoto) {
      return (
        <UserPrimaryPhotoFrame
          {...props}
          ref={ref}
          src={`${IMAGEKIT_URL}/tr:w-${parsedSize},h-${parsedSize},fo-auto,dpr-3,r-max/${
            data.primaryPhoto.rowId
          }.${data.primaryPhoto.contentType.toLowerCase()}`}
          width={parsedSize}
          height={parsedSize}
          borderRadius={size}
        />
      )
    }

    if (data.gravatarImageUrl) {
      return (
        <UserPrimaryPhotoFrame
          {...props}
          ref={ref}
          src={data.gravatarImageUrl}
          width={parsedSize}
          height={parsedSize}
          borderRadius={size}
        />
      )
    }

    return (
      <UserDefaultPhoto
        {...props}
        ref={ref}
        parsedSize={parsedSize}
        rowId={data.rowId}
        width={parsedSize}
        height={parsedSize}
        borderRadius={size}
      />
    )
  }
)

export const UserDefaultPhoto = UserPrimaryPhotoFrame.styleable<
  { rowId: string; parsedSize: number } & Omit<ImageProps, 'source'>
>(({ rowId, parsedSize, ...props }, ref) => {
  const defaultAvatar = useMemo(() => {
    const avatar = pickFromDefaults(rowId, DEFAULT_AVATARS)

    return `${IMAGEKIT_URL}/tr:w-${parsedSize},h-${parsedSize},fo-auto,dpr-3,r-max/${avatar}`
  }, [rowId, parsedSize])

  return <UserPrimaryPhotoFrame {...props} ref={ref} src={defaultAvatar} />
})

const pickFromDefaults = (
  dbRowId: string | undefined,
  defaults: string[]
): string => {
  const rowId = dbRowId?.substring(0, 8) || '0'
  return defaults[
    // convert the first part of the user's stable UUID into a number
    // then mod it by the amount of avatars we have
    // in order to select a constant avatar for a user
    parseInt(rowId, 16) % defaults.length
  ]
}
